/**
 * Session storage seam.
 *
 * The api layer reads a bearer token through this module so it stays decoupled
 * from where the token comes from. There are two sources, in precedence order:
 *
 *   1. A real OAuth session minted by signing in. Its access token lives in
 *      memory only; its refresh token and non-secret metadata persist in
 *      localStorage when available so a reload resumes the session without a
 *      fresh sign-in. The auth module owns the OAuth flow and feeds the result
 *      in here.
 *   2. A legacy developer token: localStorage under config.tokenStorageKey, or
 *      config.devToken baked into config. This is the fallback for local
 *      development against a gateway, exposed on the console as
 *      plantmewallet.setToken. A real session takes precedence over it.
 *
 * Where tokens live and why it is acceptable: the access token is held in JS
 * memory and never persisted; the refresh token and metadata persist in
 * localStorage. Both are reachable by any script that runs on this origin, so
 * the security posture rests on the strict same-origin Content-Security-Policy
 * the page sets (script-src 'self', no inline scripts, object-src 'none',
 * base-uri 'none') and the self-hosted bundle that adds no third-party script
 * origin. No provider client secret is ever in the browser: the gateway holds
 * every secret and performs the code exchange. The JWTs here are bearer
 * credentials authorizing gateway calls, not spendable key material; signing
 * stays gated by MPC and policy on the gateway. A refresh token is higher value
 * than a dev read token, so it is removed on logout and when a refresh is
 * rejected.
 */

import { config } from '@/config';
import { rememberAccountOnDevice } from '@/lib/accountOnDevice';
import { readJwtAccountIdentity, readJwtSessionId } from '@/lib/jwtClaims';
import {
  setAccount,
  clearAccount,
  setFreshLogin,
  setPasskeySignup,
  beginSession,
  restoreApprovalDecisionGuardsForCurrentAccount,
} from '@/store/store';

/** The login result recorded by {@link storeLogin}. */
export interface LoginSession {
  /** Short-lived access JWT. */
  accessToken: string;
  /** Long-lived refresh JWT. */
  refreshToken: string;
  /** Server-side session id, for metadata. */
  sessionId?: string;
  /** Organization the session is scoped to. */
  orgId: string;
  /** Provider that authenticated the user. */
  provider: string;
}

/** The rotated token pair recorded by {@link storeRefreshedPair}. */
export interface RefreshedPair {
  /** New access JWT. */
  accessToken: string;
  /** New refresh JWT (rotated on use). */
  refreshToken: string;
  /** Stable server session id returned with the rotation. */
  sessionId?: string;
}

/** The non-secret session metadata persisted alongside the refresh token. */
export interface SessionMeta {
  orgId: string | null;
  provider: string | null;
  sessionId: string | null;
}

/** Persisted metadata sentinel proving that the shared session was ended. */
const SESSION_ENDED_MARKER = '{"state":"ended","version":1}';

/**
 * The active access token. For a real session this is set by {@link storeLogin}
 * and rotated by {@link storeRefreshedPair}; it is never written to storage. For
 * the legacy fallback it is resolved once from localStorage or config. Kept in a
 * module variable, not the store, so the api layer can read it through a plain
 * function with no store-shape coupling.
 */
let currentAccess: string | null = null;

/**
 * Server session this tab explicitly adopted at login, boot resume, or a
 * successful refresh. It deliberately survives access-token suspension: shared
 * localStorage may already contain another tab's login and therefore cannot be
 * used to reconstruct this tab's owner later.
 */
let currentLocalSessionId: string | null = null;

/**
 * True once a real OAuth session is active. While true, the legacy dev-token
 * fallback is not consulted, so a stale dev token cannot shadow a real session.
 */
let realSession = false;

/**
 * True while an expired passkey session is waiting for an explicit user action
 * to reauthenticate it. The route stays mounted so a prepared transfer is not
 * discarded, but {@link getToken} returns no access token and ordinary requests
 * are stopped by the auth layer until the passkey ceremony succeeds.
 */
let passkeyReauthenticationRequired = false;

/** Last validated metadata owner available to this tab when storage is blocked. */
let currentSessionMeta: SessionMeta | null = null;

/** Last refresh owner accepted for this tab's current real session. */
let currentRefreshToken: string | null = null;

/** Refresh owner this tab explicitly adopted, independent of shared storage. */
let currentLocalRefreshToken: string | null = null;

/** Whether this tab successfully persisted or observed its current refresh owner. */
let refreshOwnerWasPersisted = false;

/** Whether this tab successfully persisted or observed any shared session owner. */
let sharedSessionOwnerWasPersisted = false;

/** Whether this tab successfully persisted or observed its metadata owner. */
let sessionMetaOwnerWasPersisted = false;

/** Whether persistence proved that this tab's shared session was invalidated. */
let persistedSessionOwnerWasInvalidated = false;

/**
 * Exact refresh value that predated a failed overwrite. `undefined` means no
 * failed-write snapshot; `null` means the key was absent before the failure.
 */
let failedRefreshWritePreviousValue: string | null | undefined;

/**
 * Exact metadata value that predated a failed overwrite. `undefined` means no
 * failed-write snapshot; `null` means the key was absent before the failure.
 */
let failedSessionMetaWritePreviousValue: string | null | undefined;

/** True once the initial fallback resolution from localStorage and config ran. */
let fallbackResolved = false;

/** Result of a storage read, preserving unavailable versus genuinely absent. */
interface StoredRead {
  value: string | null;
  available: boolean;
}

/** Read storage without collapsing an unavailable API into a missing key. */
function readStoredState(key: string): StoredRead {
  try {
    if (typeof localStorage === 'undefined') {
      return { value: null, available: false };
    }
    const value = localStorage.getItem(key);
    return {
      value: value && value.trim() !== '' ? value.trim() : null,
      available: true,
    };
  } catch {
    return { value: null, available: false };
  }
}

/** Clear cached owners after an authoritative shared-session invalidation. */
function invalidatePersistedSessionOwner(): void {
  currentSessionMeta = null;
  currentRefreshToken = null;
  refreshOwnerWasPersisted = false;
  persistedSessionOwnerWasInvalidated = true;
}

/** Reset only this tab's session state, without mutating shared persistence. */
function resetLocalSessionState(): void {
  realSession = false;
  passkeyReauthenticationRequired = false;
  currentAccess = null;
  currentLocalSessionId = null;
  currentRefreshToken = null;
  currentLocalRefreshToken = null;
  currentSessionMeta = null;
  refreshOwnerWasPersisted = false;
  sharedSessionOwnerWasPersisted = false;
  sessionMetaOwnerWasPersisted = false;
  persistedSessionOwnerWasInvalidated = false;
  failedRefreshWritePreviousValue = undefined;
  failedSessionMetaWritePreviousValue = undefined;
}

/**
 * Observe a persisted end marker or disappearance of metadata this tab had
 * previously accepted. A failed overwrite may ignore only its exact predecessor;
 * once that non-empty predecessor disappears, the change is authoritative too.
 */
function persistedMetadataInvalidatesSession(stored: StoredRead): boolean {
  if (!stored.available) return persistedSessionOwnerWasInvalidated;

  const failedPredecessor = failedSessionMetaWritePreviousValue;
  const endedMarkerIsAuthoritative =
    stored.value === SESSION_ENDED_MARKER &&
    failedPredecessor !== SESSION_ENDED_MARKER;
  const acceptedMetadataDisappeared =
    !stored.value &&
    (sessionMetaOwnerWasPersisted ||
      (failedPredecessor !== undefined && failedPredecessor !== null));

  if (endedMarkerIsAuthoritative || acceptedMetadataDisappeared) {
    invalidatePersistedSessionOwner();
    return true;
  }

  if (
    persistedSessionOwnerWasInvalidated &&
    (!stored.value || stored.value === SESSION_ENDED_MARKER)
  ) {
    return true;
  }
  return false;
}

/**
 * Read a value from localStorage, tolerating an access that throws (for example
 * a privacy mode that blocks storage).
 *
 * @param key The storage key.
 * @returns The stored value trimmed, or null when absent or unreadable.
 */
function readStored(key: string): string | null {
  return readStoredState(key).value;
}

/**
 * Write or remove a localStorage value, tolerating a storage access that throws.
 *
 * @param key The storage key.
 * @param value The value to persist, or null to remove the key.
 * @returns True when storage accepted the operation.
 */
function writeStored(key: string, value: string | null): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
    return true;
  } catch {
    // Persisting is best-effort; the in-memory state still drives this session.
    return false;
  }
}

/**
 * Resolve the legacy fallback token from its sources once, caching the result.
 * The localStorage value wins over config.devToken so a developer can override
 * the baked-in default without editing source. Only consulted when no real
 * session is active.
 *
 * @returns The resolved fallback token, or null when none is set.
 */
function resolveFallback(): string | null {
  if (fallbackResolved) return currentAccess;
  fallbackResolved = true;
  if (realSession) return currentAccess;
  // The fallback is a development convenience only. In a production build a
  // stale stored token must not masquerade as a session: it would keep the
  // account slice authenticated after a real session expires, so the boot gate
  // would keep the app shell up and fail every call instead of routing to the
  // login screen.
  if (config.flags.devTokenFallback) {
    const stored = readStored(config.tokenStorageKey);
    const fromConfig =
      config.devToken && config.devToken.trim() !== '' ? config.devToken.trim() : null;
    currentAccess = stored ?? fromConfig;
  }
  syncAccount();
  return currentAccess;
}

/**
 * Mirror the current session presence into the account store slice so the UI can
 * reflect a connected or disconnected state. Only the presence of a token and
 * the non-secret metadata are reflected; the token value is the api layer's
 * concern.
 */
function syncAccount(): void {
  // A resumed real session counts as authenticated even before an access token is
  // in memory. The access token is never persisted, so after the reload that
  // follows the OAuth callback a resumed session has only its refresh token until
  // the api layer's proactive refresh mints a fresh access token on the first
  // authed read. Gating authentication on the access token alone would bounce a
  // just-signed-in user back to the login screen on that reload. Every gateway
  // call still carries a real JWT, so this only governs which screen renders, not
  // any server-side access.
  if (realSession || currentAccess) {
    const meta = realSession ? getSessionMeta() : null;
    const accessIdentity = readJwtAccountIdentity(currentAccess);
    const refreshIdentity = realSession
      ? readJwtAccountIdentity(currentRefreshToken)
      : null;
    const identitiesAgree =
      !accessIdentity ||
      !refreshIdentity ||
      (accessIdentity.orgId === refreshIdentity.orgId &&
        accessIdentity.userId === refreshIdentity.userId);
    const tokenIdentity = identitiesAgree
      ? accessIdentity ?? refreshIdentity
      : null;
    const metadataOrgId = meta?.orgId?.toLowerCase() ?? null;
    const identityMatchesMetadata =
      tokenIdentity !== null &&
      (metadataOrgId === null || tokenIdentity.orgId === metadataOrgId);
    setAccount({
      token: currentAccess,
      authenticated: true,
      orgId: meta?.orgId ?? tokenIdentity?.orgId ?? null,
      // This is a non-authoritative UI/recovery hint only. Any disagreement
      // between available signed tokens or persisted metadata fails closed.
      userId: identityMatchesMetadata ? tokenIdentity.userId : null,
    });
    restoreApprovalDecisionGuardsForCurrentAccount();
  } else {
    clearAccount();
  }
}

/**
 * The auth-token provider the api layer installs. Returns the current bearer
 * token or null when no session is active.
 *
 * @returns The active access token, or null.
 */
export function getToken(): string | null {
  if (realSession) return currentAccess;
  return resolveFallback();
}

/** Exact server session this tab explicitly adopted. */
export function getLocalSessionId(): string | null {
  return realSession ? currentLocalSessionId : null;
}

/** Exact refresh credential this tab explicitly adopted. */
export function getLocalRefreshToken(): string | null {
  return realSession ? currentLocalRefreshToken : null;
}

/**
 * Whether a token is currently available, so the boot gate can choose between
 * the app and the login screen without reading the token value.
 *
 * @returns True when a bearer token is set.
 */
export function hasToken(): boolean {
  return getToken() !== null;
}

/**
 * Whether a real OAuth session is active, as opposed to the legacy dev-token
 * fallback. Lets the UI offer logout only for a real session.
 *
 * @returns True when signed in through OAuth.
 */
export function hasRealSession(): boolean {
  return realSession && currentAccess !== null;
}

/**
 * Whether a real OAuth session is active, regardless of whether an access token
 * is currently in memory. This is the honest "is there a session to end" test:
 * a session resumed across a reload holds only its refresh token until the first
 * proactive refresh mints an access token, so {@link hasRealSession} reads false
 * for a perfectly live session. The expiry watchdog and the reactive 401 path use
 * this to tell a genuine expired session (which must clear and route to sign-in)
 * from the legacy dev-token fallback (which never had a session to expire).
 *
 * @returns True when an OAuth session is active.
 */
export function isRealSession(): boolean {
  return realSession;
}

/** Whether this tab is preserving an expired passkey session for recovery. */
export function isPasskeyReauthenticationRequired(): boolean {
  return realSession && passkeyReauthenticationRequired;
}

/**
 * Suspend authenticated network use while retaining the persisted refresh token
 * and metadata that bind a later passkey ceremony to the same account/session.
 * This transition is intentionally distinct from logout: the account route and
 * its unsent form state stay mounted, but no expired access token can be sent.
 */
export function requirePasskeyReauthentication(): void {
  if (!realSession) return;
  currentAccess = null;
  passkeyReauthenticationRequired = true;
  syncAccount();
}

/**
 * Record a freshly minted OAuth session. The access token is held in memory; the
 * refresh token and non-secret metadata persist so a reload resumes the session.
 *
 * @param session The login result.
 */
export function storeLogin({ accessToken, refreshToken, sessionId, orgId, provider }: LoginSession): void {
  recordSession({ accessToken, refreshToken, sessionId, orgId, provider });
  // Reset both one-time onboarding flags on every login, then set the one this
  // login warrants. Resetting here is what stops a stale flag from leaking across
  // logins: a passkey signup earlier in this session set passkeySignup, and without
  // this reset a later OAuth login would still be treated as one and lose its
  // create-a-passkey offer. syncAccount above already ran, so these apply to the
  // new session.
  setPasskeySignup(false);
  setFreshLogin(false);
  // Open a new session epoch and drop the previous account's device list. Doing it
  // here rather than on logout is what covers an account swap with no logout in
  // between, which never flips `authenticated`.
  beginSession();
  // A fresh OAuth or OTP login (never a passkey) can be offered the one-time
  // create-a-passkey prompt. THIS LINE is what keeps that offer away from a passkey
  // signup — provider is 'passkey', so freshLogin stays false and the gate fails on
  // its first condition. passkeySignup, set after this returns, is a redundant
  // second guard.
  if (provider !== 'passkey') setFreshLogin(true);
  // Every route to a session ends here — passkey signup, passkey login, and the
  // OAuth exchange all call storeLogin — so this is the one place that records "an
  // account has been used in this browser" without the call sites drifting apart.
  rememberAccountOnDevice();
}

/**
 * Replace an expired passkey session after same-identity reauthentication.
 *
 * Unlike a normal login, this is continuity of the current account: it must not
 * open a new client session epoch, clear the already-loaded device list, or raise
 * onboarding flags. The caller verifies the org and user before reaching this
 * seam; this function records the new server session and token pair atomically
 * from the browser's perspective.
 *
 * @param session The newly authenticated session for the current org.
 */
export function storeReauthenticatedSession(session: LoginSession): void {
  recordSession(session);
}

/** Record the credentials and metadata common to login and reauthentication. */
function recordSession({
  accessToken,
  refreshToken,
  sessionId,
  orgId,
  provider,
}: LoginSession): void {
  realSession = true;
  passkeyReauthenticationRequired = false;
  fallbackResolved = true;
  currentAccess = accessToken && accessToken.trim() !== '' ? accessToken.trim() : null;
  const previousRefresh = readStoredState(config.refreshStorageKey);
  const previousMeta = readStoredState(config.sessionMetaStorageKey);
  const normalizedRefreshToken =
    refreshToken && refreshToken.trim() !== '' ? refreshToken.trim() : null;
  currentRefreshToken = normalizedRefreshToken;
  currentLocalRefreshToken = normalizedRefreshToken;
  currentLocalSessionId =
    sessionId?.trim() ||
    readJwtSessionId(currentAccess) ||
    readJwtSessionId(normalizedRefreshToken);
  const refreshWriteSucceeded = writeStored(
    config.refreshStorageKey,
    normalizedRefreshToken,
  );
  refreshOwnerWasPersisted = Boolean(
    normalizedRefreshToken && refreshWriteSucceeded,
  );
  failedRefreshWritePreviousValue =
    !refreshWriteSucceeded && previousRefresh.available
      ? previousRefresh.value
      : undefined;
  const meta: SessionMeta = {
    orgId: orgId ?? null,
    provider: provider ?? null,
    sessionId: currentLocalSessionId,
  };
  currentSessionMeta = meta;
  const metaWasPersisted = writeStored(
    config.sessionMetaStorageKey,
    JSON.stringify(meta),
  );
  sessionMetaOwnerWasPersisted = metaWasPersisted;
  persistedSessionOwnerWasInvalidated = false;
  sharedSessionOwnerWasPersisted = refreshOwnerWasPersisted || metaWasPersisted;
  failedSessionMetaWritePreviousValue =
    !metaWasPersisted && previousMeta.available ? previousMeta.value : undefined;
  syncAccount();
}

/**
 * Replace the access and refresh tokens after a refresh rotation, keeping the
 * existing session metadata.
 *
 * @param pair The rotated token pair.
 * @returns True only when the successor refresh token was persisted for peers.
 */
export function storeRefreshedPair({
  accessToken,
  refreshToken,
  sessionId,
}: RefreshedPair): boolean {
  realSession = true;
  passkeyReauthenticationRequired = false;
  fallbackResolved = true;
  currentAccess = accessToken && accessToken.trim() !== '' ? accessToken.trim() : null;
  currentLocalSessionId = sessionId?.trim() || currentLocalSessionId;
  const normalizedRefreshToken =
    refreshToken && refreshToken.trim() !== '' ? refreshToken.trim() : null;
  let refreshWriteSucceeded = false;
  if (normalizedRefreshToken) {
    const previousRefresh = readStoredState(config.refreshStorageKey);
    currentRefreshToken = normalizedRefreshToken;
    currentLocalRefreshToken = normalizedRefreshToken;
    refreshWriteSucceeded = writeStored(
      config.refreshStorageKey,
      normalizedRefreshToken,
    );
    refreshOwnerWasPersisted = refreshWriteSucceeded;
    failedRefreshWritePreviousValue =
      !refreshWriteSucceeded && previousRefresh.available
        ? previousRefresh.value
        : undefined;
    if (refreshWriteSucceeded) sharedSessionOwnerWasPersisted = true;
  }
  syncAccount();
  return refreshWriteSucceeded;
}

/**
 * Read the refresh owner for the active session.
 *
 * A non-empty persisted value is normally authoritative so another tab's login
 * is observed immediately. After this tab fails to overwrite the key, however,
 * the exact value captured before that write is known to be stale and the new
 * in-memory owner remains authoritative. A genuinely absent key invalidates a
 * refresh owner that was successfully shared, while storage that was never
 * available leaves the current page's in-memory session usable.
 *
 * @returns The current refresh token, or null when no owner can be established.
 */
export function getRefreshToken(): string | null {
  const storedMeta = readStoredState(config.sessionMetaStorageKey);
  if (persistedMetadataInvalidatesSession(storedMeta)) return null;

  const stored = readStoredState(config.refreshStorageKey);
  if (!stored.available) return currentRefreshToken;

  if (failedRefreshWritePreviousValue !== undefined) {
    if (stored.value === failedRefreshWritePreviousValue) {
      return currentRefreshToken;
    }

    // Any change from the captured predecessor is authoritative. In particular,
    // deletion of a predecessor that was non-empty is a cross-tab logout signal;
    // only an absent predecessor can keep an unshared in-memory session alive.
    failedRefreshWritePreviousValue = undefined;
    if (!stored.value) {
      currentRefreshToken = null;
      refreshOwnerWasPersisted = false;
      return null;
    }

    // A different non-empty owner was written after our failed overwrite. The
    // auth layer compares its signed identity with the request token before
    // allowing any authenticated call.
    currentRefreshToken = stored.value;
    refreshOwnerWasPersisted = true;
    sharedSessionOwnerWasPersisted = true;
    return currentRefreshToken;
  }

  if (stored.value) {
    currentRefreshToken = stored.value;
    refreshOwnerWasPersisted = true;
    sharedSessionOwnerWasPersisted = true;
    return currentRefreshToken;
  }

  if (refreshOwnerWasPersisted) {
    currentRefreshToken = null;
    // A refresh-only session can exist when metadata persistence failed. Once
    // that successfully shared refresh owner also disappears, storage proves
    // the local epoch ended even though there is no metadata key to carry the
    // normal tombstone/disappearance signal. Unavailable storage is not proof.
    if (storedMeta.available && !storedMeta.value) {
      persistedSessionOwnerWasInvalidated = true;
    }
  }
  refreshOwnerWasPersisted = false;
  return currentRefreshToken;
}

/**
 * Read the non-secret metadata that currently owns this tab's session.
 *
 * A valid persisted value remains authoritative so another tab's login is
 * observed immediately. A genuinely absent key also remains authoritative once
 * this tab successfully persisted metadata, because another tab may have logged
 * out. Only unavailable or invalid storage falls back to the last validated
 * in-memory owner; that preserves the documented storage-degraded session path
 * without weakening cross-tab replacement detection.
 *
 * @returns The active metadata object, or null when no owner can be established.
 */
export function getSessionMeta(): SessionMeta | null {
  const stored = readStoredState(config.sessionMetaStorageKey);
  if (persistedMetadataInvalidatesSession(stored)) return null;
  if (!stored.available) return currentSessionMeta;
  if (
    failedSessionMetaWritePreviousValue !== undefined &&
    stored.value === failedSessionMetaWritePreviousValue
  ) {
    // The metadata key still contains exactly what was present before this
    // session failed to overwrite it. Ignore that stale owner only while the
    // shared refresh owner still exists. If it disappeared, another tab could
    // have logged the partially persisted session out.
    if (getRefreshToken()) return currentSessionMeta;
    if (sharedSessionOwnerWasPersisted) currentSessionMeta = null;
    return sharedSessionOwnerWasPersisted ? null : currentSessionMeta;
  }
  failedSessionMetaWritePreviousValue = undefined;
  if (!stored.value) {
    if (getRefreshToken()) return currentSessionMeta;
    if (sharedSessionOwnerWasPersisted) currentSessionMeta = null;
    return sharedSessionOwnerWasPersisted ? null : currentSessionMeta;
  }
  try {
    const parsed = JSON.parse(stored.value) as Record<string, unknown>;
    if (parsed && typeof parsed === 'object') {
      currentSessionMeta = {
        orgId: typeof parsed.orgId === 'string' ? parsed.orgId : null,
        provider: typeof parsed.provider === 'string' ? parsed.provider : null,
        sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : null,
      };
      sessionMetaOwnerWasPersisted = true;
      sharedSessionOwnerWasPersisted = true;
      return currentSessionMeta;
    }
  } catch {
    // A corrupt metadata blob cannot replace the last owner already validated by
    // this tab only while its current signed refresh owner still exists. If that
    // owner was removed, retaining cached metadata could let an old access token
    // survive a cross-tab logout.
  }
  if (getRefreshToken()) return currentSessionMeta;
  currentSessionMeta = null;
  return null;
}

/** Whether persistence proved that this tab's shared session was invalidated. */
export function sessionPersistenceWasInvalidated(): boolean {
  return persistedSessionOwnerWasInvalidated;
}

/**
 * Resume a persisted real session on boot. The access token is not persisted, so
 * resumption marks the session as real and relies on the api layer's proactive
 * refresh to mint a fresh access token from the stored refresh token before the
 * first authed read. Returns whether a refresh token was found to resume from.
 *
 * @returns True when a persisted refresh token exists to resume.
 */
export function resumePersistedSession(): boolean {
  const storedMeta = readStoredState(config.sessionMetaStorageKey);
  if (storedMeta.value === SESSION_ENDED_MARKER) {
    invalidatePersistedSessionOwner();
    return false;
  }
  const refresh = readStored(config.refreshStorageKey);
  if (!refresh) return false;
  // A stored refresh token is itself proof that an account was used here, and this
  // path never reaches storeLogin. Recorded before the token is validated on
  // purpose: whether it still works decides if the session resumes, not whether an
  // account once existed in this browser.
  rememberAccountOnDevice();
  realSession = true;
  passkeyReauthenticationRequired = false;
  fallbackResolved = true;
  currentAccess = null;
  currentLocalRefreshToken = refresh;
  currentRefreshToken = refresh;
  currentSessionMeta = null;
  refreshOwnerWasPersisted = true;
  sharedSessionOwnerWasPersisted = true;
  sessionMetaOwnerWasPersisted = false;
  persistedSessionOwnerWasInvalidated = false;
  failedRefreshWritePreviousValue = undefined;
  failedSessionMetaWritePreviousValue = undefined;
  const meta = getSessionMeta();
  currentLocalSessionId = meta?.sessionId ?? readJwtSessionId(refresh);
  syncAccount();
  return true;
}

/**
 * Discard a real session which no longer owns shared persistence.
 *
 * The auth layer calls this only while holding the session mutation lock and
 * after binding the transition to the current local generation. No storage key
 * is written or removed: another tab may already be publishing a newer login,
 * which this stale tab does not own.
 */
export function clearStaleSessionLocally(): void {
  resetLocalSessionState();
  fallbackResolved = false;
  resolveFallback();
  syncAccount();
}

/**
 * Clear the active OAuth session: wipe the in-memory access token and persisted
 * refresh token, and replace session metadata with a non-secret ended marker.
 * The marker lets other tabs and reloads reject a stale refresh value if its
 * removal fails. The legacy dev-token key is left intact so a developer fallback
 * is not silently wiped by a logout; use {@link clearToken} to remove that.
 */
export function clearSession(): void {
  resetLocalSessionState();
  // Publish invalidation before removing the refresh owner. A peer must never
  // mistake a refresh-removal failure for proof that the old session is active.
  const markerWasPersisted = writeStored(
    config.sessionMetaStorageKey,
    SESSION_ENDED_MARKER,
  );
  if (!markerWasPersisted) {
    // Some storage implementations reject setItem while removeItem still works.
    // In that case key absence is the available invalidation signal and must be
    // published before the refresh owner disappears.
    writeStored(config.sessionMetaStorageKey, null);
  }
  writeStored(config.refreshStorageKey, null);
  // Re-resolve the legacy fallback so a configured dev token reconnects the
  // read-only client after a logout rather than dropping straight to the login
  // screen when one is present. resolveFallback is dev-gated, so in a
  // production build this resolves to no token and the account slice goes
  // unauthenticated, routing the boot gate to the login screen.
  fallbackResolved = false;
  resolveFallback();
  syncAccount();
}

/**
 * The legacy developer device fingerprint, lazily created and persisted so it is
 * stable for this browser install. It binds a session to this install in the
 * gateway's audit trail. It is an opaque random value, not a secret.
 *
 * @returns The install-scoped device fingerprint.
 */
export function getDeviceFingerprint(): string {
  const existing = readStored(config.deviceStorageKey);
  if (existing) return existing;
  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  writeStored(config.deviceStorageKey, generated);
  return generated;
}

/**
 * Set or clear the legacy developer fallback token from within the app. Persists
 * a non-empty token to localStorage so it survives a reload, and removes the key
 * when cleared. Exposed on window as plantmewallet.setToken by the boot code so a
 * developer can connect from the console without a build change. Has no effect on
 * an active real OAuth session beyond updating the fallback used after logout.
 *
 * @param token The bearer token to use, or null to disconnect.
 */
export function setToken(token: string | null): void {
  const next = token && token.trim() !== '' ? token.trim() : null;
  writeStored(config.tokenStorageKey, next);
  if (!realSession) {
    fallbackResolved = true;
    currentAccess = next;
    syncAccount();
  }
}

/** Clear the legacy developer fallback token and its persisted key. */
export function clearToken(): void {
  setToken(null);
}
